// The identity facts of the /transcripts/$id header row: source (+ batch),
// version, duration, relative time; failure facts only when set. The shell
// breadcrumb carries the transcript id (UI-LAW §2.11), never repeated here.

import type { components } from "../../api/schema";
import { displayEndReason, formatClock, relativeTime } from "../../lib/format";

type TranscriptResponse = components["schemas"]["TranscriptResponse"];

// Duration from the last timed utterance's `end_ms`; text-only transcripts
// without timings have none.
export function transcriptDurationMs(
  transcript: TranscriptResponse,
): number | null {
  let end: number | null = null;
  for (const utterance of transcript.content ?? []) {
    if (utterance.end_ms != null && (end == null || utterance.end_ms > end)) {
      end = utterance.end_ms;
    }
  }
  return end;
}

export function transcriptIdentityFacts(
  transcript: TranscriptResponse,
  batchName?: string | null,
  agentVersionTag?: string | null,
): { facts: string[]; failure: string[] } {
  const durationMs = transcriptDurationMs(transcript);
  const facts: string[] = [
    batchName ? `${transcript.source} · ${batchName}` : transcript.source,
  ];
  if (transcript.agent_version_id) {
    facts.push(`version ${agentVersionTag ?? transcript.agent_version_id}`);
  }
  if (durationMs != null) {
    facts.push(formatClock(durationMs));
  }
  facts.push(
    transcript.occurred_at
      ? `occurred ${relativeTime(transcript.occurred_at)}`
      : `created ${relativeTime(transcript.created_at)}`,
  );
  const failure: string[] = [];
  if (transcript.end_reason) {
    failure.push(displayEndReason(transcript.end_reason));
  }
  if (transcript.fail_class) {
    failure.push(transcript.fail_class);
  }
  return { facts, failure };
}
